import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "../lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * Words the field needs. This package holds no dictionary of its own (the pattern every component
 * here follows), so the application passes them in already translated.
 */
export interface DateFieldLabels {
  /** Read out for an empty field, and shown in it: "Wybierz datę". */
  placeholder: string;
  today: string;
  clear: string;
  previousMonth: string;
  nextMonth: string;
}

export interface DateFieldProps {
  /** A calendar date as `YYYY-MM-DD`, or empty. Never an instant: this is a day, not a moment. */
  value: string;
  onChange: (value: string) => void;
  labels: DateFieldLabels;
  /** Language the date is written in; the tenant's, not the browser's. */
  locale: string;
  /** Where "today" is, so the roastery's own midnight decides which day is circled. */
  timeZone: string;
  /** Earliest and latest selectable day, `YYYY-MM-DD`; days outside are shown and refused. */
  min?: string;
  max?: string;
  id?: string;
  name?: string;
  /** Hover hint, for the filter bars that name their two ends that way and nowhere else. */
  title?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  "data-testid"?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * One date field for the whole product (US-37.6, UX-09).
 *
 * <p>It replaces `<input type="date">`, which writes `mm/dd/yyyy` into a Polish screen whenever the
 * browser itself runs in English — the arrangement on most machines in a roastery, and one no
 * application setting can reach. Here the language is the tenant's, the calendar is seven by six
 * buttons (no date library), and the value stays the ISO day the API speaks.
 *
 * <p>The day grid is a real grid: arrows walk it, PageUp/PageDown change the month, Home and End
 * jump to the ends of the week, Enter picks, Escape closes (Radix returns focus to the trigger).
 * Every day carries its full date as an accessible name, because "14" alone tells a screen reader
 * nothing about which month it is in.
 */
export function DateField({
  value,
  onChange,
  labels,
  locale,
  timeZone,
  min,
  max,
  id,
  name,
  title,
  disabled,
  required,
  className,
  "data-testid": testId,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = ISO_DAY.test(value) ? value : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        title={title}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-required={required || undefined}
        data-testid={testId}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 py-1",
          "text-left text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        <span className={cn("truncate", selected ? undefined : "text-muted-foreground")}>
          {selected ? formatDay(selected, locale) : labels.placeholder}
        </span>
        <CalendarIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      </PopoverTrigger>
      {/* The value also rides in a hidden input, so a plain <form> submit and the browser's own
          autofill see the field the same way they saw the input it replaced. */}
      {name ? <input type="hidden" name={name} value={selected} /> : null}
      <PopoverContent align="start" className="w-auto p-3">
        <Calendar
          selected={selected}
          locale={locale}
          timeZone={timeZone}
          labels={labels}
          min={min}
          max={max}
          required={required}
          onSelect={(day) => {
            onChange(day);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function Calendar({
  selected,
  locale,
  timeZone,
  labels,
  min,
  max,
  required,
  onSelect,
}: {
  selected: string;
  locale: string;
  timeZone: string;
  labels: DateFieldLabels;
  min?: string;
  max?: string;
  required?: boolean;
  onSelect: (day: string) => void;
}) {
  const now = todayIn(timeZone);
  // Which day the keyboard is on. The month on screen follows it, so there is one thing to move.
  const [cursor, setCursor] = useState(() => clamp(selected || now, min, max));
  const grid = useRef<HTMLDivElement>(null);

  useEffect(() => {
    grid.current?.querySelector<HTMLButtonElement>(`[data-day="${cursor}"]`)?.focus();
  }, [cursor]);

  const days = monthGrid(cursor, firstWeekday(locale));
  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(parseDay(cursor));

  const move = (deltaDays: number) => setCursor((day) => clamp(shift(day, deltaDays), min, max));

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const steps: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    if (steps[event.key] !== undefined) {
      event.preventDefault();
      move(steps[event.key] as number);
      return;
    }
    if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      setCursor((day) => clamp(shiftMonth(day, event.key === "PageUp" ? -1 : 1), min, max));
      return;
    }
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const index = days.findIndex((day) => day === cursor);
      const week = Math.floor(index / 7);
      const edge = days[week * 7 + (event.key === "Home" ? 0 : 6)];
      if (edge) {
        setCursor(clamp(edge, min, max));
      }
    }
  };

  return (
    <div className="flex w-[17.5rem] flex-col gap-2">
      <div className="flex items-center justify-between gap-1">
        <button
          type="button"
          aria-label={labels.previousMonth}
          data-testid="date-field-previous-month"
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => setCursor((day) => clamp(shiftMonth(day, -1), min, max))}
        >
          <ChevronLeftIcon className="size-4" aria-hidden />
        </button>
        <span aria-live="polite" className="text-sm font-semibold">
          {monthLabel}
        </span>
        <button
          type="button"
          aria-label={labels.nextMonth}
          data-testid="date-field-next-month"
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => setCursor((day) => clamp(shiftMonth(day, 1), min, max))}
        >
          <ChevronRightIcon className="size-4" aria-hidden />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center text-xs text-muted-foreground">
        {weekdayNames(locale, firstWeekday(locale)).map((weekday) => (
          <abbr key={weekday.short} title={weekday.long} className="no-underline">
            {weekday.short}
          </abbr>
        ))}
      </div>

      <div ref={grid} role="grid" onKeyDown={onKeyDown} className="flex flex-col gap-0.5" data-testid="date-field-grid">
        {weeks(days).map((week) => (
          <div key={week[0]} role="row" className="grid grid-cols-7 gap-0.5">
            {week.map((day) => {
              const outside = day.slice(0, 7) !== cursor.slice(0, 7);
              const blocked = (min !== undefined && day < min) || (max !== undefined && day > max);
              return (
                <button
                  key={day}
                  type="button"
                  role="gridcell"
                  data-day={day}
                  data-testid={`date-field-day-${day}`}
                  tabIndex={day === cursor ? 0 : -1}
                  disabled={blocked}
                  aria-label={formatDay(day, locale, { dateStyle: "full" })}
                  aria-selected={day === selected}
                  aria-current={day === now ? "date" : undefined}
                  onClick={() => onSelect(day)}
                  className={cn(
                    "inline-flex size-9 items-center justify-center rounded-md text-sm transition-colors",
                    "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                    "disabled:cursor-not-allowed disabled:opacity-30",
                    outside && "text-muted-foreground",
                    day === now && day !== selected && "font-semibold text-primary",
                    day === selected && "bg-primary font-semibold text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {Number(day.slice(8, 10))}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          data-testid="date-field-today"
          className="rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-muted"
          onClick={() => onSelect(clamp(now, min, max))}
        >
          {labels.today}
        </button>
        {required ? null : (
          <button
            type="button"
            data-testid="date-field-clear"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => onSelect("")}
          >
            <XIcon className="size-3" aria-hidden />
            {labels.clear}
          </button>
        )}
      </div>
    </div>
  );
}

export interface DateRangeFieldProps
  extends Omit<DateFieldProps, "value" | "onChange" | "min" | "max" | "data-testid" | "aria-label"> {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  /** Accessible names of the two ends; there is no one name that covers both. */
  fromLabel: string;
  toLabel: string;
  /** Suffixed with `-from` and `-to` for the two fields. */
  testId?: string;
}

/**
 * Two dates that belong together: the pair every report and every filter bar draws. Each end limits
 * the other, so a range that reads backwards cannot be picked in the first place.
 */
export function DateRangeField({
  from,
  to,
  onChange,
  fromLabel,
  toLabel,
  testId,
  className,
  ...rest
}: DateRangeFieldProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <DateField
        {...rest}
        value={from}
        max={to || undefined}
        aria-label={fromLabel}
        data-testid={testId ? `${testId}-from` : undefined}
        onChange={(value) => onChange({ from: value, to })}
      />
      <span aria-hidden className="text-sm text-muted-foreground">
        –
      </span>
      <DateField
        {...rest}
        value={to}
        min={from || undefined}
        aria-label={toLabel}
        data-testid={testId ? `${testId}-to` : undefined}
        onChange={(value) => onChange({ from, to: value })}
      />
    </div>
  );
}

/** A calendar date printed in words. UTC on purpose: a business date has no time zone. */
function formatDay(
  day: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
): string {
  return new Intl.DateTimeFormat(locale, { timeZone: "UTC", ...options }).format(parseDay(day));
}

function parseDay(day: string): Date {
  return new Date(`${day}T00:00:00Z`);
}

function toDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function shift(day: string, days: number): string {
  return toDay(new Date(parseDay(day).getTime() + days * DAY_MS));
}

function shiftMonth(day: string, months: number): string {
  const date = parseDay(day);
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  // The 31st of March plus one month is the 30th of April, not the 1st of May.
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(date.getUTCDate(), lastDay));
  return toDay(target);
}

function clamp(day: string, min?: string, max?: string): string {
  if (min !== undefined && day < min) {
    return min;
  }
  if (max !== undefined && day > max) {
    return max;
  }
  return day;
}

/** Today in the given zone, as the roastery's calendar has it. */
function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** Monday in Poland and Germany, Sunday in the United States — the locale knows, when it can. */
function firstWeekday(locale: string): number {
  try {
    const info = (new Intl.Locale(locale) as Intl.Locale & { getWeekInfo?: () => { firstDay: number } })
      .getWeekInfo?.();
    return info?.firstDay ?? 1;
  } catch {
    return 1;
  }
}

function weekdayNames(locale: string, firstDay: number): { short: string; long: string }[] {
  const short = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
  const long = new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" });
  // 2024-01-01 was a Monday, so day 1 of the reference week is Monday whatever the locale says.
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(Date.UTC(2024, 0, 1 + ((firstDay - 1 + index + 7) % 7)));
    return { short: short.format(date), long: long.format(date) };
  });
}

/** The forty-two days cut into the six rows a grid needs. */
function weeks(days: string[]): string[][] {
  return Array.from({ length: 6 }, (_, index) => days.slice(index * 7, index * 7 + 7));
}

/** Six weeks starting on the locale's first weekday — always six, so the panel never jumps. */
function monthGrid(day: string, firstDay: number): string[] {
  const date = parseDay(day);
  const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  const offset = (first.getUTCDay() - firstDay + 7) % 7;
  const start = new Date(first.getTime() - offset * DAY_MS);
  return Array.from({ length: 42 }, (_, index) => toDay(new Date(start.getTime() + index * DAY_MS)));
}
